import Crop32Icon from "@mui/icons-material/Crop32";
import ImageIcon from "@mui/icons-material/Image";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PolylineIcon from "@mui/icons-material/Polyline";
import TableViewIcon from "@mui/icons-material/TableView";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Chip,
  Link,
  Typography,
} from "@mui/material";
import { ShowMore } from "components/shared/show-more";
import dayjs from "dayjs";
import { useHistory } from "react-router";
import * as Routes from "routes";
import { productCodeToExplorerLayerIdMappings } from "../constants";
import { viewDatasetInExplorer } from "./catalogue-page";

// Reverse mapping for NATIONAL X back to backend keys
const REVERSE_PRODUCT_MAPPING: Record<string, string> = {
  "NATIONAL PLANNING": "GEOSCAPE PLANNING",
  "NATIONAL PLANNING INSIGHTS": "GEOSCAPE PLANNING INSIGHTS",
  "NATIONAL BUILDINGS": "GEOSCAPE BUILDINGS",
  "NATIONAL CADASTRE": "GEOSCAPE CADASTRE",
  "NATIONAL PROPERTY": "GEOSCAPE PROPERTY",
  "NATIONAL TREES": "GEOSCAPE TREES",
  "NATIONAL ROADS": "GEOSCAPE ROADS",
  "NATIONAL AIRPORTS": "GEOSCAPE AIRPORTS",
  "NATIONAL RAIL": "GEOSCAPE RAIL",
  "NATIONAL LOCALITIES": "GEOSCAPE LOCALITIES",
  "NATIONAL LOCAL GOVERNMENT AREAS": "GEOSCAPE LOCAL GOVERNMENT AREAS",
  "NATIONAL WARDS": "GEOSCAPE WARDS",
  "NATIONAL SOLAR": "GEOSCAPE SOLAR",
  "NATIONAL SURFACE COVER": "GEOSCAPE SURFACE COVER",
  "NATIONAL GREENSPACE": "GEOSCAPE GREENSPACE",
  "NATIONAL HYDROLOGY": "GEOSCAPE HYDROLOGY",
  "NATIONAL ELECTORAL BOUNDARIES": "ELECTORAL BOUNDARIES",
  "NATIONAL POSTCODE BOUNDARIES": "POSTCODE BOUNDARIES",
};

interface CatalogueCardProps {
  productId: string;
  displayProduct: string;
  totalFeatureCount: number;
  shortDescription?: string;
  updatedDate?: string;
  image: string;
  dataType: string[];
}

export const CatalogueCard = (props: CatalogueCardProps) => {
  // Reverse map NATIONAL X back to backend key for backend operations
  const backendProductId = REVERSE_PRODUCT_MAPPING[props.productId] || props.productId;

  // Substitute PLANNING INSIGHTS for BASE version
  const datasetId =
    backendProductId === "GEOSCAPE PLANNING INSIGHTS"
      ? "GEOSCAPE PLANNING INSIGHTS - BASE"
      : backendProductId;

  const history = useHistory();

  return (
    <Card
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "row",
        padding: "0px",
      }}
    >
      <CardActionArea
        href={`${Routes.dataCatalogue}/${props.productId}`}
        LinkComponent={Link}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "row",
        }}
      >
        <CardMedia
          sx={{ height: 250, width: 250 }}
          component="img"
          src={props.image}
          title={props.displayProduct}
        />
        <Chip
          label={`${Intl.NumberFormat("en", { notation: "compact" }).format(
            props.totalFeatureCount
          )} ${humanizeFeatures(props.dataType[0])}`}
          size="medium"
          sx={{
            position: "absolute",
            top: "8px",
            left: "8px",
            textAlign: "center",
          }}
          avatar={<Avatar>{getFeatureIcon(props.dataType[0])}</Avatar>}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <CardHeader
            sx={{
              alignSelf: "flex-start",
              padding: 0,
              paddingLeft: 2,
              flexGrow: 1,
            }}
            title={
              <ShowMore lines={1} showButton={false}>
                {props.displayProduct}
              </ShowMore>
            }
          ></CardHeader>
          <CardContent sx={{ flexGrow: 1 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Box sx={{ width: "150px", display: "flex", gap: "8px" }}>
                {props.dataType.map((type) => (
                  <Chip
                    key={type}
                    label={type}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
              <Typography variant="body2">
                {props.updatedDate
                  ? `Updated: ${dayjs(props.updatedDate).format("MMM YYYY")}`
                  : ""}
              </Typography>
            </Box>
            <ShowMore lines={3} showButton={false}>
              <Typography variant="body2">{props.shortDescription}</Typography>
            </ShowMore>
          </CardContent>
          <CardActions
            sx={{
              gap: 1,
            }}
          >
            <Button
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                viewDatasetInExplorer(datasetId, history);
              }}
              disabled={!productCodeToExplorerLayerIdMappings.get(datasetId)}
            >
              View in explorer
            </Button>
            <Button
              size="small"
              color="primary"
              // component={Link}
              LinkComponent={Link}
              href={`${Routes.dataDownload}?product=${datasetId}`}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              Downloads
            </Button>
          </CardActions>
        </Box>
      </CardActionArea>
    </Card>
  );
};

const getFeatureIcon = (dataType: string) => {
  switch (dataType) {
    case "Point":
      return <LocationOnIcon fontSize="small" sx={{ color: "white" }} />;

    case "Polygon":
      return <Crop32Icon fontSize="small" sx={{ color: "white" }} />;

    case "Multi Polygon":
      return <Crop32Icon fontSize="small" sx={{ color: "white" }} />;

    case "Line":
      return <PolylineIcon fontSize="small" sx={{ color: "white" }} />;

    case "Multi Line":
      return <PolylineIcon fontSize="small" sx={{ color: "white" }} />;

    case "Raster":
      return <ImageIcon fontSize="small" sx={{ color: "white" }} />;

    case "Table":
      return <TableViewIcon fontSize="small" sx={{ color: "white" }} />;

    default:
      return <LocationOnIcon fontSize="small" sx={{ color: "white" }} />;
  }
};

const humanizeFeatures = (dataType: string) => {
  switch (dataType) {
    case "Point":
      return "Features";

    case "Polygon":
      return "Features";

    case "Multi Polygon":
      return "Features";

    case "Line":
      return "Features";

    case "Multi Line":
      return "Features";

    case "Raster":
      return "Sq KM";

    case "Table":
      return "Rows";

    default:
      return "Features";
  }
};
